/**
 * Created by Bart Decorte
 * Date: 13/09/2021
 * Time: 15:03
 */
import ChunkedUpload from './ChunkedUpload';
import toasts from '../../toasts/ToastManager';
import UploadPreviewImage from './UploadPreviewImage';
import UploadPreviewFile from './UploadPreviewFile';
import FileInfo from './FileInfo';

const EventEmitter = require('events');

export default class Dropzone extends EventEmitter {
  constructor(rootEl, params = {}) {
    super();
    this.rootEl = rootEl;
    this.preview = undefined;
    this.upload = undefined;
    this.fileInfo = new FileInfo(this.fileInfoEl(), { offset: this.indexOffset() });
    this.fileRules = params.fileRules || [];
    this.bindListeners();
  }

  bindListeners() {
    this.inputEl().addEventListener('change', ev => this.lFilesChanged(ev));
  }

  bindUploadListeners() {
    this.upload.on('canceled', data => this.lUploadCanceled(data));
    this.upload.on('failure', data => this.lUploadFailed(data));
    this.upload.on('progress', data => this.lUploadProgressed(data));
    this.upload.on('success', data => this.lUploadSucceeded(data));
  }

  bindPreviewListeners() {
    this.preview.on('canceled', data => this.lPreviewCanceled(data));
  }

  lPreviewCanceled() {
    this.cancel();
  }

  lUploadCanceled() {
    // Check, the event may lag behind, another upload could be already in progress
    if (this.upload.canceled) {
      this.preview.resetProgress();
      this.fileInfo.removeFileInfo();
    }
  }

  lUploadFailed() {
    // Check, the event may lag behind, another upload could be already in progress
    if (!this.upload.active) {
      this.emitFailure();
      this.preview.setState('fail');
    }
  }

  lUploadProgressed({ progress }) {
    this.preview.setProgress(progress);
  }

  lUploadSucceeded({ result }) {
    this.preview.setProgress(1);
    this.preview.setState('success');
    this.fileInfo.renderFilesMeta(result);
    this.emitSuccess();

    const currentUpload = this.upload;
    setTimeout(() => {
      // Check if another upload was initiated in these 3s, we wouldn't want to overwrite it's progress
      if (currentUpload === this.upload) {
        this.preview.resetProgress();
      }
    }, 3000);
  }

  async lFilesChanged(ev) {
    const inputEl = this.inputEl();
    let files = Array.from(inputEl.files);

    // Reset the file input, so the same file can be uploaded anew. This would otherwise not trigger the change event.
    this.resetFileInput();

    // Might be empty, e.g. if the user pressed "cancel" in their file finder
    if (!files.length) {
      return;
    }

    const fileErrors = await this.validateFiles(files);
    if (fileErrors.length) {
      fileErrors.reverse().forEach(error => { toasts().danger(error); });
      return;
    }

    this.push(files);
    this.setFilesSelected(true);
    this.emitChange();
    this.emitProgress();

    this.upload.execute();
  };

  push(files) {
    files = files.slice(0, 1);
    this.upload = new ChunkedUpload(files[0], {});
    const className = this.typedPreview();
    this.preview = new className(this.rootEl, { file: files[0] });
    this.preview.render();
    this.bindUploadListeners();
    this.bindPreviewListeners();
  }

  cancel() {
    this.upload.cancel();
    this.emitCancellation();
    this.fileInfo.removeFileInfo(0);
    if (! this.preview.hasStoredData()) {
      this.setFilesSelected(false);
    }
  }

  restore(filename) {
    this.preview.restore(filename)
  }

  resetFileInput() {
    const inputEl = this.inputEl();
    const parent = inputEl.parentElement;
    inputEl.remove();
    const form = document.createElement('form');
    form.appendChild(inputEl);
    form.reset();
    inputEl.remove();
    parent.prepend(inputEl);
    return this;
  };

  maxFilesize() {
    return parseInt(this.inputEl().dataset.maxFilesize);
  }

  maxFilesizeHuman() {
    return this.filesizeHuman(this.maxFilesize());
  }

  filesizeHuman(size) {
    const gigaByte = 1000 * 1000 * 1000;
    if (size >= gigaByte) {
      return `${Math.floor((size / (gigaByte / 100))) / 100}GB`;
    }

    return `${Math.floor(size / (1000 * 1000))}MB`;
  }

  async validateFiles(files) {
    let errors = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileErrors = await this.validateFile(file);
      errors = errors.concat(fileErrors);
    }

    return errors;
  }

  async validateFile(file) {
    const errors = [];
    const maxFilesize = this.maxFilesizeForMime(file.type);

    if (this.supportedMimes().length && this.supportedMimes().indexOf(file.type) === -1) {
      errors.push(`${file.name} is of an unsupported file format`);
    }

    if (file.size > maxFilesize) {
      const filesizeHuman = this.filesizeHuman(file.size);
      const maxFilesizeHuman = this.filesizeHuman(maxFilesize);
      errors.push(`${file.name} is larger (${filesizeHuman}) than the maximum supported file size for ${file.type}
        (${maxFilesizeHuman})
        <br><br>
        ${this.resizeInstructions()}
      `)
    }

    const maxFileSide = this.maxFileSideForMime(file.type);
    if (typeof maxFileSide !== typeof undefined) {
      const { width, height } = await this.imageDimensions(file);

      if (Math.max(width, height) > maxFileSide) {
        errors.push(`${file.name} is larger (${width}x${height}) than the maximum supported resolution for ${file.type}
          (${maxFileSide}x${maxFileSide})
          <br><br>
          ${this.resizeInstructions()}
        `)
      }
    }

    return errors;
  }

  resizeInstructions() {
    return `
      <i class="fe fe-info"></i> how to resize an image on
      <a target="_blank" rel="noopener" class="text-info"
        href="https://www.tenforums.com/tutorials/115131-resize-image-windows-10-photos-app.html"
      >Windows</a> or
      <a target="_blank" rel="noopener" class="text-info"
        href="https://support.apple.com/guide/preview/resize-rotate-or-flip-an-image-prvw2015/mac"
      >macOS</a>
    `;
  }

  typedPreview() {
    const previewTypes = this.previewTypes();
    return previewTypes[this.dataType()];
  }

  previewTypes() {
    return {
      'image': UploadPreviewImage,
      'file': UploadPreviewFile,
    }
  }

  emitProgress() {
    this.emit('progress', { progress: this.upload.progress() });
  }

  emitCancellation() {
    this.emit('canceled');
  }

  emitChange() {
    this.emit('change', { files: [this.upload.file] });
  }

  emitFailure() {
    this.emit('failure');
  }

  emitSuccess() {
    this.emit('success');
  }

  indexOffset() {
    return parseInt(this.rootEl.dataset.indexOffset, 10);
  }

  inputEl() {
    return this.rootEl.querySelector('input[type="file"]');
  }

  fileInfoEl() {
    return this.rootEl.querySelector('.js-file-info');
  }

  dataType() {
    return this.rootEl.dataset.type;
  }

  supportedMimes() {
    if (Array.isArray(this.fileRules)) {
      return this.fileRules;
    }

    return Object.keys(this.fileRules);
  }

  maxFilesizeForMime(mimetype) {
    const defaultMaxFilesize = this.maxFilesize();

    if (Array.isArray(this.fileRules)) {
      return defaultMaxFilesize;
    }

    if (! this.fileRules.hasOwnProperty(mimetype)) {
      return defaultMaxFilesize;
    }

    if (! this.fileRules[mimetype].hasOwnProperty('maxSize')) {
      return defaultMaxFilesize;
    }

    return Math.min(defaultMaxFilesize, this.fileRules[mimetype].maxSize);
  }

  maxFileSideForMime(mimetype) {
    if (Array.isArray(this.fileRules)) {
      return undefined;
    }

    if (! this.fileRules.hasOwnProperty(mimetype)) {
      return undefined;
    }

    if (! this.fileRules[mimetype].hasOwnProperty('maxSide')) {
      return undefined;
    }

    return this.fileRules[mimetype].maxSide;
  }

  async imageFromFile(file) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const objectUrl = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(objectUrl);
        resolve(img);
      };
      img.src = objectUrl;
    });
  }

  async imageDimensions(file) {
    return this.imageFromFile(file)
      .then((img) => {
        return {
          width: img.width,
          height: img.height,
        };
      });
  }

  setFilesSelected(hasFilesSelected) {
    const cssClass = 'no-files-selected';

    if (hasFilesSelected && this.rootEl.classList.contains(cssClass)) {
      this.rootEl.classList.remove(cssClass);
    } else if (! hasFilesSelected && ! this.rootEl.classList.contains(cssClass)) {
      this.rootEl.classList.add(cssClass);
    }
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/components/uploads/Dropzone.js